import { useState, useEffect } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { FinanceReport } from '../../types/financeReport';
import { FormInput } from '../ui/FormInput';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { formatNumber } from '../../utils/format';
import { WorshipReport } from '../../types/worshipReport';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

interface FinanceReportFormProps {
  onSubmit?: (data: FinanceReport) => void;
  onClose?: () => void;
}

export default function FinanceReportForm({ onSubmit, onClose }: FinanceReportFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<FinanceReport>>({
    date: '',
    worshipReportId: '',
    meetingTypeId: '',
    finances: {
      tithes: 0,
      regularOfferings: 0,
      specialOfferings: 0
    },
    notes: '',
    needsNotes: ''
  });

  // Auto-populate date and meeting type from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        setFormData(prev => ({ ...prev, date: '', meetingTypeId: '' }));
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date,
            meetingTypeId: worshipReportData.meetingTypeId
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  const calculateTotal = () => {
    if (!formData.finances) return 0;
    return formData.finances.tithes + formData.finances.regularOfferings + formData.finances.specialOfferings;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.meetingTypeId || !formData.finances) {
        throw new Error('Missing required fields');
      }
      
      if (!formData.worshipReportId) {
        throw new Error('Veuillez sélectionner le rapport de culte à annexer');
      }

      const totalFinances = calculateTotal();
      
      const reportData = {
        ...formData,
        totalFinances,
        createdBy: user?.id,
        createdAt: new Date()
      } as FinanceReport;

      const docRef = await addDoc(collection(db, 'financeReports'), reportData);
      
      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'finance',
          department: 'Finance',
          needsNotes: formData.needsNotes,
          reportDate: formData.date!
        });
      }
      
      if (onSubmit) {
        onSubmit({ ...reportData, id: docRef.id });
      }

      toast.success('Rapport financier enregistré avec succès');
    } catch (error) {
      console.error('Error saving finance report:', error);
      toast.error('Erreur lors de l\'enregistrement du rapport financier');
    }
  };

  const handleFinancesChange = (field: keyof FinanceReport['finances'], value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      finances: {
        ...prev.finances!,
        [field]: numValue
      }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations générales */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Informations générales</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <WorshipReportSelect
              value={formData.worshipReportId || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, worshipReportId: value }))}
              required={true}
              departmentCollection="financeReports"
            />
          </div>
          <FormInput
            label="Date du culte"
            type="date"
            value={formData.date || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            disabled={!!formData.worshipReportId}
          />
          <MeetingTypeSelect
            value={formData.meetingTypeId || ''}
            onChange={(value) => setFormData(prev => ({ ...prev, meetingTypeId: value }))}
            required={false}
            disabled={!!formData.worshipReportId}
          />
        </div>
      </div>

      {/* Rapport financier */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Rapport financier</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormInput
            type="number"
            min="0"
            label="Dîmes"
            value={formData.finances?.tithes || 0}
            onChange={(e) => handleFinancesChange('tithes', e.target.value)}
          />
          <FormInput
            type="number"
            min="0"
            label="Offrandes ordinaires"
            value={formData.finances?.regularOfferings || 0}
            onChange={(e) => handleFinancesChange('regularOfferings', e.target.value)}
          />
          <FormInput
            type="number"
            min="0"
            label="Offrandes spéciales"
            value={formData.finances?.specialOfferings || 0}
            onChange={(e) => handleFinancesChange('specialOfferings', e.target.value)}
          />
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            Total des entrées: <span className="text-lg font-bold text-[#00665C]">{formatNumber(calculateTotal())} FCFA</span>
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Notes / Observations
        </label>
        <textarea
          placeholder="Observations financières..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.notes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
        />
      </div>

      {/* Besoins */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Liste des besoins
        </label>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.needsNotes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          Enregistrer le rapport financier
        </button>
      </div>
    </form>
  );
}